import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const faqs = [
  { question: 'How do I import my existing contacts?', answer: 'Go to Contacts page and click the Import button. Upload a CSV file with columns: first_name, last_name, email, phone, title. The system will map the columns automatically.' },
  { question: 'Can I customize my sales pipeline stages?', answer: 'Yes! Go to Settings then Pipeline. You can add, rename, reorder, and delete stages to match your exact sales process.' },
  { question: 'How do I invite team members?', answer: 'Go to Settings then Team and click Invite Member. Enter their email address and they will receive an invitation to join your organization.' },
  { question: 'What happens to deleted records?', answer: 'Deleted contacts, accounts, and deals are moved to Trash (accessible from the sidebar). They can be recovered within 30 days before permanent deletion.' },
  { question: 'How does the AI Voice feature work?', answer: 'AI Voice is available on the CRM + AI Voice plan. It allows you to make AI-assisted sales calls with automated scripts, call recording, and post-call insights.' },
  { question: 'Can I export my data?', answer: 'Yes, you can export data from Accounts, Contacts, Deals, and Reports pages using the Export button. Data is exported as CSV format.' },
  { question: 'How do I upgrade my plan?', answer: 'Go to Billing in the sidebar to view available plans and upgrade. Changes take effect immediately.' },
  { question: 'Is my data secure?', answer: 'Yes. Wilcart CRM uses enterprise-grade security with encrypted data storage, secure HTTPS connections, and row-level security to ensure your data is only accessible to authorized users.' }
];

const ContactSupport = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '', subject: '', category: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const navigate = useNavigate();

  const handleMenuToggle = () => setIsSidebarOpen(!isSidebarOpen);
  const handleSidebarClose = () => setIsSidebarOpen(false);

  const handleSubmit = (e) => {
    e?.preventDefault();
    window.location.href = `mailto:support@wilcart.com?subject=${encodeURIComponent(formData?.subject || 'Support Request')}&body=${encodeURIComponent('Name: ' + formData?.name + '\nEmail: ' + formData?.email + '\nCategory: ' + formData?.category + '\n\n' + formData?.message)}`;
    setSubmitted(true);
  };

  const categoryOptions = [
    { value: '', label: 'Select a category' },
    { value: 'billing', label: 'Billing & Payments' },
    { value: 'technical', label: 'Technical Issue' },
    { value: 'feature', label: 'Feature Request' },
    { value: 'account', label: 'Account & Settings' },
    { value: 'data', label: 'Data & Import/Export' },
    { value: 'other', label: 'Other' }
  ];

  return (
    <>
      <Helmet>
        <title>Contact Support - Wilcart CRM</title>
        <meta name="description" content="Get help from the Wilcart CRM support team." />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header onMenuToggle={handleMenuToggle} isSidebarOpen={isSidebarOpen} />
        <Sidebar isOpen={isSidebarOpen} onClose={handleSidebarClose} />
        <main className="lg:ml-64 pt-24 lg:pt-28">
          <div className="p-4 lg:p-6 max-w-5xl">
            <div className="mb-8">
              <div className="flex items-center space-x-3 mb-2">
                <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center">
                  <Icon name="Headphones" size={20} className="text-primary" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-foreground">Contact Support</h1>
                  <p className="text-muted-foreground text-sm">We're here to help. Reach out and we'll get back to you quickly.</p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
              <div className="bg-card border border-border rounded-xl p-5 flex items-start space-x-4">
                <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Icon name="Mail" size={20} className="text-blue-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground mb-1">Email Support</h3>
                  <p className="text-sm text-muted-foreground mb-2">Response within 24 hours</p>
                  <a href="mailto:support@wilcart.com" className="text-sm text-primary hover:underline font-medium">support@wilcart.com</a>
                </div>
              </div>

              <div className="bg-card border border-border rounded-xl p-5 flex items-start space-x-4">
                <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Icon name="MessageCircle" size={20} className="text-green-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground mb-1">Live Chat</h3>
                  <p className="text-sm text-muted-foreground mb-2">Mon–Fri, 9am–6pm EST</p>
                  <span className="inline-flex items-center text-xs px-2 py-1 bg-green-100 text-green-700 rounded-full">
                    <span className="w-1.5 h-1.5 bg-green-500 rounded-full mr-1.5 animate-pulse" />
                    Online now
                  </span>
                </div>
              </div>

              <div className="bg-card border border-border rounded-xl p-5 flex items-start space-x-4">
                <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Icon name="BookOpen" size={20} className="text-purple-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground mb-1">Documentation</h3>
                  <p className="text-sm text-muted-foreground mb-2">Self-service guides and tutorials</p>
                  <button onClick={() => navigate('/documentation')} className="text-sm text-primary hover:underline font-medium">Browse docs</button>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-card border border-border rounded-xl p-6">
                <h2 className="text-lg font-semibold text-foreground mb-5">Send a Message</h2>
                {submitted ? (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Icon name="CheckCircle" size={32} className="text-success" />
                    </div>
                    <h3 className="text-lg font-semibold text-foreground mb-2">Message Sent!</h3>
                    <p className="text-muted-foreground text-sm mb-4">Your email client should have opened. We'll get back to you within 24 hours.</p>
                    <Button variant="outline" onClick={() => setSubmitted(false)}>Send Another</Button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-1">Your Name</label>
                        <input type="text" required value={formData?.name} onChange={e => setFormData(p => ({...p, name: e?.target?.value}))}
                          className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-background" placeholder="John Smith" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-1">Email</label>
                        <input type="email" required value={formData?.email} onChange={e => setFormData(p => ({...p, email: e?.target?.value}))}
                          className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-background" placeholder="you@company.com" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1">Category</label>
                      <select value={formData?.category} onChange={e => setFormData(p => ({...p, category: e?.target?.value}))}
                        className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-background">
                        {categoryOptions?.map(opt => <option key={opt?.value} value={opt?.value}>{opt?.label}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1">Subject</label>
                      <input type="text" required value={formData?.subject} onChange={e => setFormData(p => ({...p, subject: e?.target?.value}))}
                        className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-background" placeholder="Brief description of your issue" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1">Message</label>
                      <textarea required rows={5} value={formData?.message} onChange={e => setFormData(p => ({...p, message: e?.target?.value}))}
                        className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-background resize-none" placeholder="Describe your issue in detail..." />
                    </div>
                    <Button type="submit" className="w-full">
                      <Icon name="Send" size={16} className="mr-2" />
                      Send Message
                    </Button>
                  </form>
                )}
              </div>

              <div>
                <h2 className="text-lg font-semibold text-foreground mb-5">Frequently Asked Questions</h2>
                <div className="space-y-3">
                  {faqs?.map((faq, i) => (
                    <div key={i} className="bg-card border border-border rounded-xl overflow-hidden">
                      <button
                        onClick={() => setOpenFaq(openFaq === i ? null : i)}
                        className="w-full flex items-center justify-between p-4 text-left hover:bg-muted/30 transition-colors"
                      >
                        <span className="text-sm font-medium text-foreground pr-4">{faq?.question}</span>
                        <Icon name={openFaq === i ? 'ChevronUp' : 'ChevronDown'} size={16} className="text-muted-foreground flex-shrink-0" />
                      </button>
                      {openFaq === i && (
                        <div className="px-4 pb-4">
                          <p className="text-sm text-muted-foreground leading-relaxed">{faq?.answer}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </>
  );
};

export default ContactSupport;
